import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useNavigate } from "react-router";
import { useContasReceberListaPagination } from "../hooks/contas_receber_lista/pagination";
import { processError } from "../../../../common/utils/base_error";
import { initialContasReceberFiltersValues, useContasReceberListaFilters } from "../hooks/contas_receber_lista/filters";
import { ContasReceberLista } from "../../domain/models/contas_receber_lista";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { ContasRecebimentoForm } from "../schemas/ContasReceber_lista/ContasReceber_recebimento";
import { ContasReceberCopiaForm } from "../schemas/ContasReceber_lista/ContasReceber_copia";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { ReportContasReceberAnaliticoColumnsForm, ReportContasReceberAnaliticoForm } from "../schemas/reports/contasReceberAnaliticoReport";
import { ContasReceberAnaliticoFilter } from "../../domain/models/contas_receber_analitico_filter";
import { ClienteDropDown } from "../../../../common/domain/models/cliente_dropdown";
import { ValoresContasMes } from "../../../comercial/domain/models/valores_contas_mes";

type LoadContasReceberListaState = {

    idsCentroCusto?: number[];
    idsCliente?: number[];
    idsBanco?: number[];
    idsOrdemCompra?: number[];
    idsSubconta?: number[];
    status?: number | null;
    titulo?: string | null;
    dataReceberDe?: Date | null;
    dataReceberAte?: Date | null;
    dataRecebidoDe?: Date | null;
    dataRecebidoAte?: Date | null;
    dataCriacaoDe?: Date | null;
    dataCriacaoAte?: Date | null;
    idContratoFinanceiro?: number | null;
    nroConta?: number | null;

    page?: number;
    rowsPerPage?: number;
}


export const useContasReceberListaViewModel = () => {

    const {
        reports: {
            getReportContaReceberUseCase,
            getReportContasReceberAnaliticoUseCase,
        },
        financeiro: {
            getAllContasReceberListaUseCase,
            deleteContaReceberByIdUseCase,
            recebimentoContasUseCase,
            copiaContasReceberUseCase,
            saveContasReceberAnaliticoFiltrosUseCase,
            getContasReceberAnaliticoFiltrosUseCase,


        },
        commons: {
            getCentroCustoDropDownUseCase,
            getClientesDropDownUseCase,
            getBancosPagamentoDropDownUseCase,
            getOrdemComprasDropDownUseCase,
            getSubcontasDropDownUseCase,

        },

    } = useDependencyInjection();

    const { pushErrorNotification, pushWarnNotification, pushInfoNotification, setPageLoading } = usePageLoader();
    const [reportViewerState, startReportViewerProcess] = useReportViewer();

    const debouncedRef = useCreateDebounceHandler(1500);

    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>();

    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [selectedIdContasReceber, setSelectedIdContasReceber] = useState<number>();

    const [openCadastroRecebimentoModal, setOpenCadastroRecebimentoModal] = useState(false);
    const [valorTotalRecebido, setValorTotalRecebido] = useState(0);

    const [openCopiaContaReceberModal, setOpenCopiaContaReceberModal] = useState(false);
    const [selectedIdContaReceberCopia, setSelectedIdContaReceberCopia] = useState<number>();
    const [tituloContaReceberCopia, setTituloContaReceberCopia] = useState<string>();

    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [errorMessage, setErrorMessage] = useState<string>();
    const [contasReceberLista, setContasReceberLista] = useState<ContasReceberLista[]>();

    const filters = useContasReceberListaFilters();
    const pagination = useContasReceberListaPagination();

    const [valoresContasMes, setValoresContasMes] = useState<ValoresContasMes | undefined>();

    const [openReportContasReceberAnaliticoModal, setOpenReportContasReceberAnaliticoModal] = useState(false);
    const [filtroContasReceberAnalitico, setFiltroContasReceberAnalitico] = useState<ContasReceberAnaliticoFilter>();

    //DROPDOWN ESTADOS

    const [ddlCentroCusto, setDdlCentroCusto] = useState<DropDown[]>();
    const [ddlCliente, setDdlCliente] = useState<ClienteDropDown[]>();
    const [ddlBanco, setDdlBanco] = useState<DropDown[]>();
    const [ddlOrdemCompra, setDdlOrdemCompra] = useState<DropDown[]>();
    const [ddlSubconta, setDdlSubconta] = useState<DropDown[]>();

    //#region CARREGAMENTO.


    const loadContasReceberListaState = useCallback(async (funcState?: LoadContasReceberListaState) => {

        try {

            const filteredTitulo = evaluateParamValue(funcState?.titulo, filters.titulo)
            const filteredDataReceberDe = evaluateParamValue(funcState?.dataReceberDe, filters.dataReceberDe);
            const filteredDataReceberAte = evaluateParamValue(funcState?.dataReceberAte, filters.dataReceberAte);
            const filteredDataBaixaDe = evaluateParamValue(funcState?.dataRecebidoDe, filters.dataRecebidoDe);
            const filteredDataBaixaAte = evaluateParamValue(funcState?.dataRecebidoAte, filters.dataRecebidoAte);
            const filteredStatus = evaluateParamValue(funcState?.status, filters.status);
            const filteredIdBancoPagamento = funcState?.idsBanco ?? filters.idsBanco;
            const filteredIdCliente = funcState?.idsCliente ?? filters.idsCliente;
            const filteredIdCentroCusto = funcState?.idsCentroCusto ?? filters.idsCentroCusto;
            const filteredIdSubconta = funcState?.idsSubconta ?? filters.idsSubconta;
            const filteredIdContratoFinanceiro = evaluateParamValue(funcState?.idContratoFinanceiro, filters.idContratoFinanceiro);
            const filteredDataCriacaoDe = evaluateParamValue(funcState?.dataCriacaoDe, filters.dataCriacaoDe);
            const filteredDataCriacaoAte = evaluateParamValue(funcState?.dataCriacaoAte, filters.dataCriacaoAte);
            const filteredIdOrdemCompra = funcState?.idsOrdemCompra ?? filters.idsOrdemCompra;
            const filteredNroConta = evaluateParamValue(funcState?.nroConta, filters.nroConta);


            const filteredPage = funcState?.page ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            const indicadores = await getAllContasReceberListaUseCase.getAllContasReceberLista(
                {
                    titulo: filteredTitulo ?? "",
                    dataReceberDe: filteredDataReceberDe,
                    dataReceberAte: filteredDataReceberAte,
                    dataRecebidoDe: filteredDataBaixaDe,
                    dataRecebidoAte: filteredDataBaixaAte,
                    status: filteredStatus,
                    idsCentroCusto: filteredIdCentroCusto,
                    idsBanco: filteredIdBancoPagamento,
                    idsCliente: filteredIdCliente,
                    idsSubconta: filteredIdSubconta,
                    idContratoFinanceiro: filteredIdContratoFinanceiro,
                    dataCriacaoDe: filteredDataCriacaoDe,
                    dataCriacaoAte: filteredDataCriacaoAte,
                    idsOrdemCompra: filteredIdOrdemCompra,
                    nroConta: filteredNroConta,
                },
                filteredPage,
                filteredRowsPerPage
            );

            setContasReceberLista(getAllContasReceberListaUseCase.items);
            setValoresContasMes(indicadores);

            pagination.setRowsPerPage(getAllContasReceberListaUseCase.pageSize);
            pagination.setTotalItems(getAllContasReceberListaUseCase.totalItems);
        } catch (e: unknown) {
            setContasReceberLista([]);
            throw new Error("Falha ao buscar contas a receber!");
        }
    }, [
        filters,
        pagination,
        getAllContasReceberListaUseCase,
    ]);

    const loadCentroCustoDropDown = useCallback(async () => {

        try {
            setPageLoading(true);
            const result = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
            setDdlCentroCusto(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar centro de custo!'));
        } finally {
            setPageLoading(false);
        }

    }, [getCentroCustoDropDownUseCase, setPageLoading, pushErrorNotification]);

    const loadClientesDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const result = await getClientesDropDownUseCase.getClientesDropDown();
            setDdlCliente(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar clientes!'));
        } finally {
            setPageLoading(false);
        }
    }, [getClientesDropDownUseCase, setPageLoading, pushErrorNotification]);

    const loadBancosPagamentoDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const result = await getBancosPagamentoDropDownUseCase.getBancosPagamentoDropDown();
            setDdlBanco(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar bancos!'));
        } finally {
            setPageLoading(false);
        }
    }, [
        getBancosPagamentoDropDownUseCase,
        setPageLoading,
        pushErrorNotification
    ]);

    const loadOrdemCompraDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const result = await getOrdemComprasDropDownUseCase.getOrdemComprasDropDown();
            setDdlOrdemCompra(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar ordens de compra!'));
        } finally {
            setPageLoading(false);
        }
    }, [
        getOrdemComprasDropDownUseCase,
        setPageLoading,
        pushErrorNotification
    ]);

    const loadSubcontaDropDown = useCallback(async () => {
        try {
            setPageLoading(true);

            const result = await getSubcontasDropDownUseCase.getSubcontasDropDown();
            setDdlSubconta(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar subcontas!'));
        } finally {
            setPageLoading(false);
        }
    }, [
        getSubcontasDropDownUseCase,
        setPageLoading,
        pushErrorNotification
    ]);


    //#endregion




    //#region PAGE START.
    usePageStartBootstrap(async () => {
        try {
            if (contasReceberLista === undefined) {
                await loadContasReceberListaState();
                await loadCentroCustoDropDown();
                await loadClientesDropDown();
                await loadBancosPagamentoDropDown();
                await loadOrdemCompraDropDown();
                await loadSubcontaDropDown();

            }
        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao buscar lista de contas a receber!")[0]);
        }
    })
    //#endregion



    const handleContasReceberEdit = useCallback((idContasReceber: number | null) => {
        navigate('/ContasReceber_cadastro', { state: { idContasReceber } });
    }, [navigate]);

    const handleNewContasReceber = useCallback(() => {
        navigate("/ContasReceber_cadastro");
    }, [navigate]);


    //#region FUNÇÕES RELATÓRIO

    const handleContaReceberExportar = useCallback(async (idContaReceber: number) => {
        try {
            setLoading(true);
            await startReportViewerProcess({
                fetchCallback: async (mimeType) => {
                    return await getReportContaReceberUseCase.execute(idContaReceber, mimeType);
                }
            });

        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao gerar relatório de Conta a Receber!'));
        } finally {
            setLoading(false);
        }
    }, [
        getReportContaReceberUseCase,
        startReportViewerProcess,
        pushErrorNotification,
    ]);

    const handleReportContasReceberAnaliticoSubmit = useCallback(async (form: ReportContasReceberAnaliticoForm) => {
        try {
            setLoading(true)
            await startReportViewerProcess({
                fetchCallback: async (mimeType) => {
                    return await getReportContasReceberAnaliticoUseCase.execute(
                        {
                            requiredMimeType: mimeType,
                            centroCusto: form.centroCusto,
                            subconta: form.subconta,
                            titulo: form.titulo,
                            status: form.status,
                            tipoConta: form.tipoConta,
                            idUsuario: form.idUsuario,
                            dataCriacaoDe: form.dataCriacaoDe,
                            dataCriacaoAte: form.dataCriacaoAte,
                            dataCompetenciaDe: form.dataCompetenciaDe,
                            dataCompetenciaAte: form.dataCompetenciaAte,
                            cliente: form.cliente,
                            nroContratoFinanceiro: form.nroContratoFinanceiro,
                            dataReceberDe: form.dataReceberDe,
                            dataReceberAte: form.dataReceberAte,
                            dataRecebidoDe: form.dataRecebidoDe,
                            dataRecebidoAte: form.dataRecebidoAte,
                            ordenacao: form.ordenacao,
                            colunas: form.colunas,
                        });
                }
            });

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao carregar filtros do relatório!'));

        } finally {
            setLoading(false);
        }
    }, [
        getReportContasReceberAnaliticoUseCase,
        pushErrorNotification,
        startReportViewerProcess
    ]);


    const handleReportContasReceberAnaliticoColumnsSubmit = useCallback(async (form: ReportContasReceberAnaliticoColumnsForm) => {
        try {
            setLoading(true)
            await saveContasReceberAnaliticoFiltrosUseCase.saveContasReceberAnaliticoFiltros({
                nroContasPagar: form.nroContasPagar,
                centroCusto: form.centroCusto,
                bancoPagamento: form.bancoPagamento,
                notaFiscal: form.notaFiscal,
                nroDocumento: form.nroDocumento,
                status: form.status,
                titulo: form.titulo,
                dataPagar: form.dataPagar,
                dataPago: form.dataPago,
                valorPagar: form.valorPagar,
                valorPago: form.valorPago,
                observacao: form.observacao,
                subconta: form.subconta,
                cliente: form.cliente,
                nroContratoFinanceiro: form.nroContratoFinanceiro,
                multa: form.multa,
                juros: form.juros,
                desconto: form.desconto,
                bdi: form.bdi,
                bdi2: form.bdi2,
                bdiTotal: form.bdiTotal,
                bdiTotal2: form.bdiTotal2,
                dataCriacao: form.dataCriacao,
                dataCompetencia: form.dataCompetencia
            });
            pushInfoNotification('Sucesso', 'Configurações salvas com sucesso!', { isAutoFinish: true });

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao carregar filtros do relatório!'));
        } finally {
            setLoading(false);
        }
    }, [
        saveContasReceberAnaliticoFiltrosUseCase,
        pushErrorNotification,
        pushInfoNotification
    ]);


    const loadContasReceberRelatorioFiltros = useCallback(async () => {
        try {

            const filtros = await getContasReceberAnaliticoFiltrosUseCase.execute();
            setFiltroContasReceberAnalitico(filtros ?? undefined);

        } catch (e: unknown) {
            setFiltroContasReceberAnalitico(undefined);
            throw e;
        }
    }, [
        getContasReceberAnaliticoFiltrosUseCase,
    ]);

    const handleOpenReportContasReceberAnaliticoModal = useCallback(async (openState: boolean) => {
        setOpenReportContasReceberAnaliticoModal(openState);
        try {
            setLoading(true);
            if (openState) {
                await loadContasReceberRelatorioFiltros();
                setOpenReportContasReceberAnaliticoModal(openState);
            } else {
                setOpenReportContasReceberAnaliticoModal(openState);
            }

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao carregar filtros do relatório!'));
        }
        finally {
            setLoading(false);
        }
    }, [
        loadContasReceberRelatorioFiltros,
        pushErrorNotification,
    ]);


    //#endregion


    //#region PAGINAÇÃO.

    const handleReload = useCallback(async () => {
        try {
            setLoading(true);
            await loadContasReceberListaState();
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de contas a receber!'));
        } finally {
            setLoading(false);
        }
    }, [loadContasReceberListaState, pushErrorNotification]);

    const handleRowSelectionModelChange = useCallback((rows: GridRowSelectionModel) => {
        setRowSelectionModel(rows);
    }, []);

    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);
            await loadContasReceberListaState({ page: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de contas a receber!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadContasReceberListaState, pushErrorNotification]);

    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);
            await loadContasReceberListaState({ rowsPerPage, page: 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar contas a receber!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadContasReceberListaState, pushErrorNotification]);

    //#endregion




    //#region DELECAO
    const handleContasReceberDeleteOpenState = useCallback((idContasReceber: number | null, openState: boolean) => {
        if (openState) {
            if (idContasReceber && contasReceberLista) {
                setSelectedIdContasReceber(contasReceberLista.find(x => x.idContasReceber === idContasReceber)?.idContasReceber);
            } else {
                setSelectedIdContasReceber(undefined);
            }
        }
        setOpenDeleteModal(openState);
    }, [contasReceberLista]);

    const handleConfirmarDeletarContasReceber = useCallback(async () => {
        try {

            setSaving(true);
            if (selectedIdContasReceber) {
                await deleteContaReceberByIdUseCase.deleteContaReceberByIds([selectedIdContasReceber], "lista");
                pushInfoNotification("Sucesso", "Conta Deletada com sucesso!", { isAutoFinish: true });

            } else {
                if (rowSelectionModel) {
                    const idContasReceber: number[] = [];
                    for (let row of rowSelectionModel.ids.values()) {
                        if (typeof row === 'number') {
                            idContasReceber.push(row);
                        } else if (typeof row === 'string') {
                            try {
                                idContasReceber.push(Number(row));
                            } catch (e) {
                            }
                        }
                    }
                    await deleteContaReceberByIdUseCase.deleteContaReceberByIds(idContasReceber, "lista");
                    pushInfoNotification("Sucesso", "Conta Deletada com sucesso!", { isAutoFinish: true });
                }
            }

            await loadContasReceberListaState();

        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao Deletar Contas!"));
        } finally {
            setSaving(false);
            setOpenDeleteModal(false);
            setSelectedIdContasReceber(undefined)
            setRowSelectionModel({ type: 'include', ids: new Set() });
        }
    }, [
        selectedIdContasReceber,
        deleteContaReceberByIdUseCase,
        loadContasReceberListaState,
        rowSelectionModel,
        pushErrorNotification,
        pushInfoNotification]);


    //#endregion




    //#region PESQUISA.

    const handleIdCentroCustoChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsCentroCusto: newValues })
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasReceberListaState({ idsCentroCusto: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a receber!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadContasReceberListaState,]
    );

    const handleIdClienteChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsCliente: newValues })
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasReceberListaState({ idsCliente: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a receber!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadContasReceberListaState,]
    );

    const handleIdBancoChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsBanco: newValues })
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasReceberListaState({ idsBanco: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a receber!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadContasReceberListaState,]
    );

    const handleIdOrdemCompraChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsOrdemCompra: newValues })
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasReceberListaState({ idsOrdemCompra: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a receber!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadContasReceberListaState,]
    );

    const handleIdSubcontaChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setFilters({ idsSubconta: newValues })
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasReceberListaState({ idsSubconta: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a receber!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadContasReceberListaState,]
    );

    const handleIdContratoFinanceiroChange = useDebouncedCallback(
        debouncedRef,
        (idContratoFinanceiro: string | null) => {
            const id = idContratoFinanceiro ? Number(idContratoFinanceiro) : null;
            filters.setFilters({ idContratoFinanceiro: id });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasReceberListaState({ idContratoFinanceiro: id, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a receber!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [loadContasReceberListaState, filters]
    );

    const handleTituloChange = useDebouncedCallback(
        debouncedRef,
        (titulo: string | null) => {
            filters.setFilters({ titulo });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasReceberListaState({ titulo, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a receber!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [loadContasReceberListaState, filters]
    );

    const handleDataRecebidoRangeChange = useDebouncedCallback(
        debouncedRef,
        (dataRecebidoDe: Date | null, dataRecebidoAte: Date | null) => {
            filters.setFilters({ dataRecebidoDe });
            filters.setFilters({ dataRecebidoAte });

            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasReceberListaState({ dataRecebidoDe, dataRecebidoAte, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a receber!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [loadContasReceberListaState, filters]
    );

    const handleDataReceberRangeChange = useDebouncedCallback(
        debouncedRef,
        (dataReceberDe: Date | null, dataReceberAte: Date | null) => {
            filters.setFilters({ dataReceberDe });
            filters.setFilters({ dataReceberAte });

            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasReceberListaState({ dataReceberDe, dataReceberAte, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a receber!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [loadContasReceberListaState, filters]
    );

    const handleDataCriacaoRangeChange = useDebouncedCallback(
        debouncedRef,
        (dataCriacaoDe: Date | null, dataCriacaoAte: Date | null) => {
            filters.setFilters({ dataCriacaoDe });
            filters.setFilters({ dataCriacaoAte });
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadContasReceberListaState({ dataCriacaoDe, dataCriacaoAte, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar contas a receber!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [loadContasReceberListaState, filters]
    );

    const handleStatusChange = useDebouncedCallback(debouncedRef, (status: number | null) => {
        useContasReceberListaFilters.setState({ status })
        return async () => {
            try {
                setLoading(true);
                pagination.setPage(1);
                await loadContasReceberListaState({ status, page: 1 });
            } finally {
                setLoading(false);
            }
        };
    }, [loadContasReceberListaState]);


    const handleNroContaChange = useDebouncedCallback(debouncedRef, (nroConta: string | null) => {
        const normalizedNroConta = nroConta ? parseInt(nroConta, 10) : null;
        useContasReceberListaFilters.setState({ nroConta: normalizedNroConta })
        return async () => {
            try {
                setLoading(true);
                pagination.setPage(1);
                await loadContasReceberListaState({ nroConta: normalizedNroConta, page: 1 });
            } finally {
                setLoading(false);
            }
        };
    }, [loadContasReceberListaState]);


    const handleClearFilters = useCallback(async () => {
        try {
            setLoading(true);
            filters.clearFilters();
            pagination.setPage(1);
            await loadContasReceberListaState({
                ...initialContasReceberFiltersValues,
                page: 1
            });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao buscar contas a receber!'));
        } finally {
            setLoading(false);
        }
    }, [filters, pagination, loadContasReceberListaState, pushErrorNotification]);

    //#endregion 





    //#region COPIA
    const handleContaReceberCopiaOpenState = useCallback((idContasReceber: number | null, openState: boolean) => {
        if (openState) {
            if (idContasReceber && contasReceberLista) {
                setSelectedIdContaReceberCopia(idContasReceber);
                setTituloContaReceberCopia(contasReceberLista.find(x => x.idContasReceber === idContasReceber)?.nome ?? '');
                setOpenCopiaContaReceberModal(true);
            } else {
                setSelectedIdContaReceberCopia(undefined);
            }
        }
    }, [contasReceberLista]);

    const handleSubmitContaReceberCopia = useCallback(async (form: ContasReceberCopiaForm) => {

        try {
            if (selectedIdContaReceberCopia) {
                setSaving(true);

                await copiaContasReceberUseCase.copiaContasReceber(form)
                pushInfoNotification("Sucesso", "Conta a Receber copiada com sucesso!", { isAutoFinish: true });
                await loadContasReceberListaState();

            } else {
                pushWarnNotification("Atenção", "Nenhuma conta a receber selecionada para cópia!");
                return;
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao copiar Conta a Receber!"));
        } finally {
            setOpenCopiaContaReceberModal(false);
            setSelectedIdContaReceberCopia(undefined);
            setTituloContaReceberCopia('');
            setSaving(false);
        }
    }, [
        selectedIdContaReceberCopia,
        copiaContasReceberUseCase,
        loadContasReceberListaState,
        pushErrorNotification,
        pushWarnNotification,
        pushInfoNotification,
    ]);

    //#endregion




    //#region RECEBIMENTO

    const calculaTotalRecebido = useCallback(() => {
        if (rowSelectionModel && contasReceberLista) {
            let total = 0;
            for (let row of rowSelectionModel.ids.values()) {
                let idRow: number | null = null;
                if (typeof row === 'number') {
                    idRow = row;
                } else if (typeof row === 'string') {
                    try {
                        idRow = Number(row);
                    } catch (e) {
                    }
                }
                if (idRow) {
                    const conta = contasReceberLista.find(x => x.idContasReceber === idRow);
                    if (conta) {
                        total += conta.valorReceber ?? 0;;
                    }
                }
            }
            setValorTotalRecebido(total);
        } else {
            setValorTotalRecebido(0);
        }
    }, [rowSelectionModel, contasReceberLista]);

    const handleOpenCadastroRecebimentoModal = useCallback((openState: boolean) => {
        if (openState) {
            if (!rowSelectionModel || rowSelectionModel.ids.size === 0) {
                pushWarnNotification("Atenção", "Selecione as contas a serem recebidas!");
                return;
            }
        }
        calculaTotalRecebido();
        setOpenCadastroRecebimentoModal(openState);
    }, [
        rowSelectionModel,
        pushWarnNotification,
        calculaTotalRecebido,
    ]);


    const handleCadastroRecebimentoSubmit = useCallback(async (form: ContasRecebimentoForm) => {
        try {
            setSaving(true);
            if (rowSelectionModel) {

                const idContasReceber: number[] = [];
                for (let row of rowSelectionModel.ids.values()) {
                    if (typeof row === 'number') {
                        idContasReceber.push(row);
                    } else if (typeof row === 'string') {
                        try {
                            idContasReceber.push(Number(row));
                        } catch (e) {
                        }
                    }
                }
                await recebimentoContasUseCase.recebimentoContas({
                    ...form,
                    idsContaReceber: idContasReceber,
                })
                pushInfoNotification("Sucesso", "Contas Recebidas com sucesso!", { isAutoFinish: true });
                await loadContasReceberListaState();

            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao Receber Contas!"));
        } finally {
            setSaving(false);
            setOpenCadastroRecebimentoModal(false);
            setRowSelectionModel({ type: 'include', ids: new Set() });
        }
    }, [
        rowSelectionModel,
        recebimentoContasUseCase,
        loadContasReceberListaState,
        pushErrorNotification,
        pushInfoNotification,
    ]);

    //#endregion


    return {
        loading,
        saving,
        contasReceberLista: contasReceberLista ?? [],
        errorMessage,
        filters,
        pagination,
        openDeleteModal,
        ddlCentroCusto: ddlCentroCusto ?? [],
        ddlCliente: ddlCliente ?? [],
        ddlBanco: ddlBanco ?? [],
        ddlSubconta: ddlSubconta ?? [],
        ddlOrdemCompra: ddlOrdemCompra ?? [],
        openCadastroRecebimentoModal,
        rowSelectionModel,
        valorTotalRecebido,
        openCopiaContaReceberModal,
        selectedIdContaReceberCopia,
        tituloContaReceberCopia,
        reportViewerState,
        openReportContasReceberAnaliticoModal,
        filtroContasReceberAnalitico,
        valoresContasMes,

        handleOpenReportContasReceberAnaliticoModal,
        handleReportContasReceberAnaliticoSubmit,
        handleReportContasReceberAnaliticoColumnsSubmit,
        handleSubmitContaReceberCopia,
        setOpenCopiaContaReceberModal,
        setSelectedIdContaReceberCopia,
        handleCadastroRecebimentoSubmit,
        handleRowSelectionModelChange,
        handleOpenCadastroRecebimentoModal,
        setOpenCadastroRecebimentoModal,
        handleContaReceberCopiaOpenState,
        handleReload,
        handlePageChange,
        handleRowsPerPageChange,
        handleStatusChange,
        handleClearFilters,
        handleContasReceberEdit,
        handleContasReceberDeleteOpenState,
        handleNewContasReceber,
        handleConfirmarDeletarContasReceber,
        handleIdCentroCustoChange,
        handleIdClienteChange,
        handleIdBancoChange,
        handleIdOrdemCompraChange,
        handleIdSubcontaChange,
        handleIdContratoFinanceiroChange,
        handleTituloChange,
        handleDataReceberRangeChange,
        handleDataCriacaoRangeChange,
        handleDataRecebidoRangeChange,
        handleContaReceberExportar,
        handleNroContaChange,


    };
}
